import { useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  DateTimePickerAndroid,
  DateTimePickerEvent,
} from '@react-native-community/datetimepicker'
import { BottomView } from '@zet/common/components/bottomView'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import { useModal } from '@zet/common/hooks/useModal'
import { colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B1, B2, B5, H5 } from '@zet/design-system/typography'
import { useExperiments } from '@zet/experiments'
import { i18n } from '@zet/localization'
import { PINCODE_REGEX } from '@zet/utils/regularExpression'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { VerifyCustomerDetailsParams } from '../../api/functions'
import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useFetchCustomerDetails,
} from '../../api/hooks'
import useVerifyCustomerDetails from '../../api/hooks/useVerifyCustomerDetails'
import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'

const CustomerBasicDetails = ({
  route,
}: {
  route: {
    params: {
      customerId: number
    }
  }
}) => {
  const { data: experimentsData } = useExperiments()

  const validationSchema = useMemo(() => {
    return z
      .object({
        email: z.string().email({ message: 'Invalid email' }),
        pinCode: z
          .string()
          .min(1, { message: 'Field is required!' })
          .length(6, { message: 'Not a valid PIN code' })
          .regex(PINCODE_REGEX, 'Invalid Pin Code'),
        dob: z.string({ required_error: 'Please select a Date' }),
        salaryType: z.string({ required_error: 'Please select a type' }),
        ...(!experimentsData?.UI_isAddCustomerNewFlow
          ? {
              panNumber: z
                .string()
                .min(1, { message: 'Field is required!' })
                .regex(
                  new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
                  'Invalid PAN',
                ),
            }
          : {}),
      })
      .required()
  }, [experimentsData?.UI_isAddCustomerNewFlow])

  const [salaryType, setSalaryType] = useState<string>('')
  const { openModal } = useModal()
  const today = new Date()
  const [date, setDate] = useState<Date>(today)

  type ValidationSchema = z.infer<typeof validationSchema>

  const { data: prefilledData } = useFetchCustomerDetails(
    route?.params?.customerId,
  )
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    setError,
    trigger,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    if (prefilledData?.modeOfIncome) {
      if (prefilledData?.modeOfIncome === 'Cash') {
        setSalaryType('Cash')
        setValue('salaryType', 'Cash')
      } else if (prefilledData?.modeOfIncome === 'Bank') {
        setSalaryType('Bank')
        setValue('salaryType', 'Bank')
      }
    }

    if (!experimentsData?.UI_isAddCustomerNewFlow && prefilledData?.panNo) {
      setValue('panNumber', prefilledData?.panNo)
    }
    if (prefilledData?.dob) {
      setValue('dob', moment(prefilledData?.dob).format('DD/MM/YYYY'))
    }
    if (prefilledData?.pincode) {
      setValue('pinCode', prefilledData?.pincode)
    }
    if (prefilledData?.email) {
      setValue('email', prefilledData?.email)
    }

    if (
      !experimentsData?.UI_isAddCustomerNewFlow &&
      prefilledData &&
      prefilledData?.panNo
    ) {
      trigger()
    }
  }, [prefilledData, experimentsData?.UI_isAddCustomerNewFlow])

  const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'number') {
        return { message: 'Field is required!' }
      }
    }

    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'string') {
        return { message: 'Field is required!' }
      }
    }
    return { message: ctx.defaultError }
  }

  z.setErrorMap(customErrorMap)

  const onChange = (
    event: DateTimePickerEvent,
    selectedDate: Date | undefined,
  ) => {
    const value = selectedDate || new Date()
    setDate(value)
    setValue('dob', moment(value).format('DD/MM/YYYY'), {
      shouldValidate: true,
      shouldTouch: true,
    })
  }

  const showMode = () => {
    DateTimePickerAndroid.open({
      value: date || undefined,
      onChange,
      mode: 'date',
      maximumDate: new Date(),
    })
  }
  const onsubmit = () => {
    const { panNumber, email, pinCode, dob, salaryType } = getValues()
    const requestBodyData = {
      customerId: route.params.customerId,
      modeOfIncome: salaryType === 'Cash' ? 2 : 1,
      dob: moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      pinCode: pinCode,
      email: email,
    }
    if (experimentsData?.UI_isAddCustomerNewFlow) {
      mutateWithExperiments(requestBodyData as IRequestData)
    } else {
      mutate(requestBodyData as VerifyCustomerDetailsParams)
    }
  }

  const { mutate: mutateWithExperiments, isLoading: isExperimentsLoading } =
    useAddCustomerNextState()

  const {
    mutate,
    isLoading,
    isSuccess: customerVerifySuccess,
    data,
  } = useVerifyCustomerDetails()

  useEffect(() => {
    if (!experimentsData?.UI_isAddCustomerNewFlow && customerVerifySuccess) {
      if (
        data?.error?.code === 2014 ||
        data?.error?.code === 2015 ||
        data?.error?.code === 2016
      )
        setError('panNumber', { type: 'focus', message: data?.error?.message })
    }
  }, [customerVerifySuccess, data?.error?.code, data?.error?.message, setError])

  return (
    <LazyHOC>
      <AddCustomerBackHandlerHOC>
        <KeyBoardWrapper
          className="flex-1 bg-white "
          BottomSheet={
            <BottomView
              onPress={handleSubmit(onsubmit)}
              stepCount={2}
              maxCount={!experimentsData?.UI_isAddCustomerNewFlow ? 3 : 4}
              disabled={!isValid}
              loading={isLoading || isExperimentsLoading}
              buttonEvent={{
                eventName: 'click_action',
                payload: { item_name: 'add_customer_step_2_continue' },
              }}
            />
          }
        >
          <Stack className="px-3">
            <Stack className="h-3" />

            <H5>{i18n.t('customerDetails.basicCustomerDetails')}</H5>
            {!experimentsData?.UI_isAddCustomerNewFlow && (
              <>
                <Stack className="mt-[28]">
                  <B2 className=" text-grays-80">
                    {i18n.t('customerDetails.pan')}
                    <B1 className="text-error-100 ml-1">*</B1>
                  </B2>

                  <Controller
                    control={control}
                    render={({
                      field: { onChange, onBlur, value },
                      fieldState: { isTouched },
                    }) => (
                      <Input
                        placeholder="Eg: BHWIC2448S"
                        onBlur={onBlur}
                        onChangeText={(value) => {
                          onChange(value)
                        }}
                        value={value}
                        type={
                          errors?.panNumber?.message && isTouched
                            ? 'error'
                            : errors?.panNumber?.message ===
                                'PAN already exists'
                              ? 'warning'
                              : 'primary'
                        }
                        autoCapitalize="characters"
                        maxLength={10}
                      />
                    )}
                    name="panNumber"
                  />
                  {errors.panNumber?.message ? (
                    <Stack className="mt-2">
                      <B5 className="text-error-100">
                        {errors.panNumber?.message}
                      </B5>
                    </Stack>
                  ) : null}
                </Stack>

                <Stack className=" mt-4/2 rounded-lg bg-[#ECFFEC] pb-2  ">
                  <Alert
                    intent="success"
                    iconLeft={{
                      name: 'SecureOutlined',
                      color: zetColors.success[150],
                    }}
                    className=" text-grays-60 "
                  >
                    {i18n.t('customerDetails.whyPan')}
                  </Alert>
                  <Button
                    onPress={() =>
                      openModal('YoutubeVideoModal', {
                        videoId: 'Q5PmwtJLUrs',
                        videoData: {
                          en: { videoId: 'Q5PmwtJLUrs' },
                          hi: { videoId: 'JCJSv0xCHcc' },
                        },
                      })
                    }
                    iconLeft={<Icon type="VideoOutlined" size={16} />}
                    intent="primary"
                    outlined={true}
                    className="border-grays-60 mx-0  ml-[38] h-4 w-[120] rounded-xl px-1 py-[1.5]"
                    textStyle=" text-xs font-[Graphie-SemiBold] text-grays-60  "
                    eventName="click_action"
                    eventPayload={{ item_name: 'add_customer_why_pan_video' }}
                  >
                    {i18n.t('customerDetails.watch')}
                  </Button>
                </Stack>
              </>
            )}

            <Stack className="mt-3">
              <B2 className=" text-grays-80 mb-1">
                {i18n.t('customerDetails.income')}
                <B1 className="text-error-100 ml-1">*</B1>
              </B2>
              <XStack>
                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur } }) => (
                    <XStack>
                      <Pressable
                        onBlur={onBlur}
                        onPress={() => {
                          onChange('Bank')
                          setSalaryType('Bank')
                        }}
                        eventName="click_action"
                        eventPayload={{
                          item_name: 'add_customer_select_income_mode',
                          income_type: 'Bank',
                        }}
                      >
                        <XStack
                          className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Bank' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                        >
                          {salaryType !== 'Bank' ? (
                            <Icon type="BankAccount" size={36}></Icon>
                          ) : (
                            <Icon type="BankAccountWithColor" size={36}></Icon>
                          )}
                          <B2 className="ml-4/2 text-grays-60">
                            {i18n.t('customerDetails.bank')}
                          </B2>
                        </XStack>
                      </Pressable>
                      <Pressable
                        onBlur={onBlur}
                        onPress={() => {
                          onChange('Cash')
                          setSalaryType('Cash')
                        }}
                        eventName="click_action"
                        eventPayload={{
                          item_name: 'add_customer_select_income_mode',
                          income_type: 'Cash',
                        }}
                      >
                        <XStack
                          className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Cash' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                        >
                          {salaryType !== 'Cash' ? (
                            <Icon type="Cash" size={36}></Icon>
                          ) : (
                            <Icon type="CashWithColor" size={36}></Icon>
                          )}
                          <B2 className="ml-4/2 text-grays-60">
                            {i18n.t('customerDetails.cash')}
                          </B2>
                        </XStack>
                      </Pressable>
                    </XStack>
                  )}
                  name="salaryType"
                />
              </XStack>
              {errors.salaryType?.message ? (
                <Stack className="mt-2">
                  <B5 className="text-error-100">
                    {errors.salaryType?.message}
                  </B5>
                </Stack>
              ) : null}
            </Stack>

            <Stack className="mt-4">
              <B2 className="text-grays-80 mb-2">
                {i18n.t('customerDetails.dob')}
                <B1 className="text-error-100 ml-1">*</B1>
              </B2>
              <Controller
                control={control}
                name="dob"
                render={({ field: { onBlur, onChange, value } }) => (
                  <Pressable onPress={showMode}>
                    <Input
                      isEditable={false}
                      placeholder="Select Date"
                      iconRight={
                        <Icon type="ArrowDown" color={zetColors.grays[80]} />
                      }
                      iconLeft={
                        <Icon
                          type="DayOutlined"
                          color={zetColors.grays[50]}
                          className="mr-2 border"
                        />
                      }
                      className="justify-center"
                      style={{
                        color:
                          date === today
                            ? zetColors.grays[50]
                            : zetColors.grays[100],
                      }}
                      value={value}
                    />
                  </Pressable>
                )}
              />
            </Stack>

            <Stack className="mt-4">
              <B2 className="text-grays-80 -mb-4">
                {i18n.t('customerDetails.pin')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>

              <Controller
                control={control}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: {},
                }) => (
                  <Input
                    iconLeft={
                      <Icon
                        type="LocationPointerOutlined"
                        color={zetColors.grays[50]}
                      ></Icon>
                    }
                    placeholder="Eg: 560035"
                    onBlur={onBlur}
                    onChangeText={(value) => {
                      onChange(value)
                    }}
                    keyboardType="numeric"
                    value={value}
                    maxLength={6}
                  />
                )}
                name="pinCode"
              />
              {errors.pinCode?.message ? (
                <Stack className="mt-2">
                  <B5 className="text-error-100">{errors.pinCode?.message}</B5>
                </Stack>
              ) : null}
            </Stack>

            <Stack className="mb-5 mt-4">
              <B2 className=" text-grays-80">
                {i18n.t('customerDetails.email')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>

              <Controller
                control={control}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: {},
                }) => (
                  <Input
                    placeholder="Eg: username@email.com"
                    onBlur={onBlur}
                    onChangeText={(value) => {
                      onChange(value.trim())
                    }}
                    value={value}
                  />
                )}
                name="email"
              />
              {errors.email?.message ? (
                <Stack className="mt-2">
                  <B5 className="text-error-100">{errors.email?.message}</B5>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </KeyBoardWrapper>
      </AddCustomerBackHandlerHOC>
    </LazyHOC>
  )
}

export default CustomerBasicDetails
